import { PlatformLocation } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ClientSelectors } from 'src/app/features/client';
import {
  PackageUser,
  PackageUsersActions,
  PackageUsersSelectors,
} from 'src/app/features/package-users';
import { Package } from 'src/app/features/packages';
import { IdImages } from 'src/app/features/participant-verification/models';
import { ParticipantVerificationSelectors } from 'src/app/features/participant-verification/store';

import { RootStoreState } from 'src/app/store';

import { ModalsActions } from 'src/app/features/modals';
import { IdSides } from './idSide.enum';
import { UntypedFormControl } from '@angular/forms';
import { SelectOption } from 'src/app/features/shared';
import { MatSelectChange } from '@angular/material/select';

@Component({
    selector: 'app-id-verify',
    templateUrl: './id-verify.component.html',
    styleUrls: ['./id-verify.component.scss'],
    standalone: false
})
export class IdVerifyComponent implements OnInit, OnDestroy {
  static identifier = 'IdVerifyComponent';

  participants$: Observable<Array<PackageUser>>;
  idImages$: Observable<IdImages>;

  selectedParticipantId = new UntypedFormControl('');
  particpantOptions$: Observable<SelectOption[]>;

  showFrontImage: boolean;
  showBackImage: boolean;
  selectedRadioButton = '1';

  selectedParticipantSubscription: Subscription;

  imageWrapperRotation = 0;
  get imageWrapperStyle() {
    return `transform: rotate(${this.imageWrapperRotation}deg)`;
  }

  @ViewChild('idImage') idImage: ElementRef;
  @ViewChild('imageWrapper') imageWrapper: ElementRef;

  constructor(
    public location: PlatformLocation,
    private readonly store: Store<RootStoreState.State>
  ) {}

  ngOnInit(): void {
    this.loadParticipants();
  }

  ngOnDestroy() {
    this.selectedParticipantSubscription?.unsubscribe();
  }

  closeModal(): void {
    this.store.dispatch(ModalsActions.ClearModalComponent());
  }

  private loadParticipants() {
    this.store.dispatch(
      PackageUsersActions.FetchPackageUsers({
        payload: { ignoreIfLoaded: true },
      })
    );
    this.participants$ = this.store.select(PackageUsersSelectors.getNonSigningAgents);
    this.particpantOptions$ = this.participants$.pipe(
      map((participants) =>
        participants?.map((p) => ({
          value: p.packageUserGuid,
          label: `${p.firstName} ${p.lastName}`,
        }))
      )
    );

    this.selectedParticipantSubscription = this.participants$
      .pipe(
        tap((participants) => {
          this.selectedParticipantId.setValue(participants[0].packageUserGuid);
          this.getIdImagesForParticipant();
        })
      )
      .subscribe();
  }

  onParticipantChange() {
    this.getIdImagesForParticipant();
  }

  private getIdImagesForParticipant() {
    this.idImages$ = this.store.pipe(
      select(ClientSelectors.shouldBypassRealId),
      switchMap((shouldBypassRealId) =>
        this.store.pipe(
          select(ParticipantVerificationSelectors.getIdImagesForParticipant, {
            packageUserGuid: this.selectedParticipantId.value,
            shouldBypassRealId,
          })
        )
      )
    );
    this.toggleIdImages(IdSides.FRONT);
  }

  viewIdImage() {
    if (this.selectedRadioButton === '1') {
      this.toggleIdImages(IdSides.FRONT);
    } else if (this.selectedRadioButton === '2') {
      this.toggleIdImages(IdSides.BACK);
    }
  }

  private toggleIdImages(idSide: string) {
    if (idSide === IdSides.FRONT) {
      this.showFrontImage = true;
      this.showBackImage = false;
    } else if (idSide === IdSides.BACK) {
      this.showFrontImage = false;
      this.showBackImage = true;
    }

    requestAnimationFrame(() =>
      this.setImageWrapperWidth(this.imageWrapper.nativeElement, this.idImage.nativeElement)
    );
  }

  onRotate(idImage: HTMLImageElement): void {
    this.imageWrapperRotation -= 90;

    this.setImageWrapperWidth(this.imageWrapper.nativeElement, idImage);
  }

  setImageWrapperWidth(
    imageWrapper: HTMLElement,
    { naturalHeight, naturalWidth }: HTMLImageElement
  ): void {
    imageWrapper.style.width = naturalWidth >= naturalHeight ? '100%' : '50%';
  }
}
